/**
 * <cart-empty-wishlist> lists the shopper's most recently wishlisted pieces in the empty
 * cart drawer. Rendered by snippets/cart-empty-nav.liquid, loaded by snippets/cart-drawer.liquid
 * so it is defined before the drawer re-renders into its empty state.
 *
 * The wishlist is a list of product handles that assets/wishlist.js keeps in localStorage,
 * synced with the customer metafield when logged in, so guests see theirs too.
 *
 * Rows link to the product page instead of quick-adding: sizes, the pre-order line item
 * property and the Mystery Box questionnaire are all chosen there.
 */
if (!customElements.get("cart-empty-wishlist")) {
  customElements.define(
    "cart-empty-wishlist",
    class CartEmptyWishlist extends HTMLElement {
      connectedCallback() {
        // wishlist.js merges the account's list into storage as it loads; wait for it.
        if (document.readyState === "loading") {
          document.addEventListener("DOMContentLoaded", () => this.render(), { once: true });
        } else {
          this.render();
        }
      }

      get handles() {
        const wishlist = window.MinimogTheme && window.MinimogTheme.Wishlist;
        if (wishlist && Array.isArray(wishlist.products)) return wishlist.products;
        try {
          return JSON.parse(localStorage.getItem("m-wishlist-products")) || [];
        } catch (e) {
          return [];
        }
      }

      async render() {
        const limit = Number(this.dataset.limit) || 4;
        // New handles are appended, so the newest are last.
        const handles = this.handles.slice(-limit).reverse();
        if (!handles.length) return;

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        const products = await Promise.all(
          handles.map((handle) =>
            fetch(`${root}products/${encodeURIComponent(handle)}.js`)
              .then((response) => (response.ok ? response.json() : null))
              .catch(() => null)
          )
        );

        const rows = products.filter(Boolean).map((product) => this.buildRow(product));
        if (!rows.length || !this.isConnected) return;

        this.querySelector("[data-wishlist-list]").replaceChildren(...rows);
        const all = this.querySelector("[data-wishlist-all]");
        const wishlistUrl = window.MinimogSettings && window.MinimogSettings.routes && window.MinimogSettings.routes.wishlist_url;
        if (all && wishlistUrl) all.href = wishlistUrl;
        this.hidden = false;
      }

      buildRow(product) {
        const row = this.querySelector("template[data-wishlist-row]").content.firstElementChild.cloneNode(true);

        row.querySelectorAll("[data-link]").forEach((link) => (link.href = product.url));
        row.querySelector("[data-title]").textContent = product.title;
        row.querySelector("[data-price]").textContent = product.available ? this.price(product) : "Sold out";

        const image = row.querySelector("[data-image]");
        if (product.featured_image) {
          const separator = product.featured_image.includes("?") ? "&" : "?";
          image.src = `${product.featured_image}${separator}width=160`;
        } else {
          image.remove();
        }

        return row;
      }

      price(product) {
        const format = window.MinimogSettings && window.MinimogSettings.money_format;
        const amount =
          typeof window.formatMoney === "function" && format
            ? window.formatMoney(product.price, format)
            : `$${(product.price / 100).toFixed(2)}`;
        return product.price_varies ? `From ${amount}` : amount;
      }
    }
  );
}
